import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, CheckCircle2, Loader2, User, Building2, Mail, Phone } from 'lucide-react';
import { format, addDays, setHours, setMinutes } from 'date-fns';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';

interface TimeSlot {
  date: Date;
  time: string;
  available: boolean;
  dateKey: string;
  displayDate: string;
  isBeingBooked?: boolean; // Track if slot is currently being booked
}

interface ContactInfo {
  name: string;
  businessName: string;
  email: string;
  phone: string;
}

// 🔒 CRITICAL: LocalStorage key for persisting confirmed bookings across page reloads
const CONFIRMED_BOOKINGS_KEY = 'partner_calendar_confirmed_bookings';

// Toast notification function
const showToast = (message: string, type: 'success' | 'error' = 'success') => {
  const toastContainer = document.getElementById('toast-container');
  if (!toastContainer) {
    const container = document.createElement('div');
    container.id = 'toast-container';
    container.style.cssText = 'position: fixed; top: 20px; right: 20px; z-index: 9999;';
    document.body.appendChild(container);
  }

  const toast = document.createElement('div');
  toast.style.cssText = `
    padding: 12px 20px;
    margin-bottom: 10px;
    border-radius: 8px;
    color: white;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
    animation: slideIn 0.3s ease-out;
    ${type === 'success' ? 'background: linear-gradient(135deg, #10b981 0%, #059669 100%);' : 'background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);'}
  `;
  toast.textContent = message;

  const container = document.getElementById('toast-container');
  if (container) {
    container.appendChild(toast);
    setTimeout(() => {
      toast.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => toast.remove(), 300);
    }, 3000);
  }
};

export default function PartnerCalendarScheduling() {
  const navigate = useNavigate();
  
  const [selectedSlot, setSelectedSlot] = useState<TimeSlot | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [availableSlots, setAvailableSlots] = useState<TimeSlot[]>([]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(true);
  const [contactInfo, setContactInfo] = useState<ContactInfo>({
    name: '',
    businessName: '',
    email: '',
    phone: ''
  });
  const [errors, setErrors] = useState<Partial<ContactInfo>>({});
  const [bookingInProgress, setBookingInProgress] = useState<string | null>(null);
  
  // 🔒 NEW: Initialize from localStorage to persist across page reloads
  const [confirmedBookings, setConfirmedBookings] = useState<Set<string>>(() => {
    try {
      const stored = localStorage.getItem(CONFIRMED_BOOKINGS_KEY);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Clean up old bookings (older than 24 hours)
        const now = new Date().getTime();
        const filtered = parsed.filter((isoString: string) => {
          const bookingTime = new Date(isoString).getTime();
          return bookingTime > now - (24 * 60 * 60 * 1000);
        });
        return new Set(filtered);
      }
    } catch (error) {
      console.error('Error loading confirmed bookings from localStorage:', error);
    }
    return new Set();
  });

  // 🔒 NEW: Sync confirmedBookings to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(
        CONFIRMED_BOOKINGS_KEY,
        JSON.stringify(Array.from(confirmedBookings))
      );
    } catch (error) {
      console.error('Error saving confirmed bookings to localStorage:', error);
    }
  }, [confirmedBookings]);

  // Fetch booked slots and generate available time slots
  const fetchAvailableSlots = useCallback(async (forceRefresh = false) => {
    setIsLoadingSlots(true);
    try {
      // Add a small delay on force refresh to ensure database transaction is committed
      if (forceRefresh) {
        await new Promise(resolve => setTimeout(resolve, 300));
      }

      // Fetch all existing bookings from calendar_bookings
      const { data: bookedSlots, error: bookingError } = await supabase
        .from('calendar_bookings')
        .select('selected_time_slot')
        .order('selected_time_slot', { ascending: true });

      if (bookingError) {
        console.error('Error fetching booked slots:', bookingError);
      }

      // 🔒 CRITICAL: Merge database bookings with local confirmed bookings
      const dbBookedTimes = new Set(
        bookedSlots?.map(booking => 
          new Date(booking.selected_time_slot).toISOString()
        ) || []
      );
      
      // Combine database bookings + local cache for absolute accuracy
      const allBookedTimes = new Set([...dbBookedTimes, ...confirmedBookings]);

      // Fetch schedule blocks
      const { data: scheduleBlocksData, error: blocksError } = await supabase
        .from('admin_schedule_blocks')
        .select('blocked_date, start_time, end_time')
        .order('blocked_date', { ascending: true });

      if (blocksError) {
        console.error('Error fetching schedule blocks:', blocksError);
      }

      console.log('🔍 Schedule blocks fetched:', scheduleBlocksData); // Debug log

      // Create a set of blocked time ranges - with improved time parsing
      const blockedRanges = (scheduleBlocksData || []).map((block: any) => {
        // Parse times properly - database returns time strings like "09:00:00" or "09:00:16.8"
        const startTimeParts = block.start_time.split(':');
        const endTimeParts = block.end_time.split(':');
        
        // Extract hours and minutes, ignoring seconds/microseconds
        const startHour = parseInt(startTimeParts[0]);
        const startMinute = parseInt(startTimeParts[1]);
        const endHour = parseInt(endTimeParts[0]);
        const endMinute = parseInt(endTimeParts[1]);
        
        return {
          date: block.blocked_date,
          startHour,
          startMinute,
          endHour,
          endMinute,
          rawStartTime: block.start_time,
          rawEndTime: block.end_time
        };
      });

      console.log('🔍 Processed blocked ranges:', blockedRanges); // Debug log

      // Generate available time slots (next 14 business days)
      const slots: TimeSlot[] = [];
      const today = new Date();
      
      for (let dayOffset = 1; dayOffset <= 14; dayOffset++) {
        const date = addDays(today, dayOffset);
        const dayOfWeek = date.getDay();
        
        // Skip weekends
        if (dayOfWeek === 0 || dayOfWeek === 6) continue;
        
        // Business hours: 9 AM to 8 PM (extended from 5 PM)
        for (let hour = 9; hour <= 19; hour++) {
          const slotTime = setMinutes(setHours(date, hour), 0);
          const slotISO = slotTime.toISOString();
          const slotDate = format(slotTime, 'yyyy-MM-dd');
          
          // 🔒 CRITICAL: Check against combined booking set
          const isBooked = allBookedTimes.has(slotISO);
          
          // 🔒 FIXED: Check if slot HOUR falls within any blocked time range
          // A slot at hour H should be blocked if ANY block overlaps with the time period [H:00, H+1:00)
          const isBlocked = blockedRanges.some(block => {
            // First check if dates match
            if (block.date !== slotDate) return false;
            
            // Calculate the time range for this slot (full hour)
            const slotStartMinutes = hour * 60; // Start of slot hour (e.g., 13:00 = 780)
            const slotEndMinutes = (hour + 1) * 60; // End of slot hour (e.g., 14:00 = 840)
            
            // Calculate blocked time range in minutes
            const blockStartMinutes = block.startHour * 60 + block.startMinute;
            const blockEndMinutes = block.endHour * 60 + block.endMinute;
            
            // Check if blocked range overlaps with this slot's hour
            // Overlap occurs if: block_start < slot_end AND block_end > slot_start
            const overlaps = blockStartMinutes < slotEndMinutes && blockEndMinutes > slotStartMinutes;
            
            if (overlaps) {
              console.log(`🚫 Slot ${slotDate} ${hour}:00 BLOCKED due to overlap with ${block.rawStartTime}-${block.rawEndTime}`, {
                slotRange: `${hour}:00 - ${hour+1}:00`,
                slotMinutes: `${slotStartMinutes} - ${slotEndMinutes}`,
                blockMinutes: `${blockStartMinutes} - ${blockEndMinutes}`,
                blockTime: `${block.rawStartTime} - ${block.rawEndTime}`
              });
            }
            
            return overlaps;
          });
          
          slots.push({
            date: slotTime,
            time: format(slotTime, 'HH:mm'),
            available: !isBooked && !isBlocked,
            dateKey: format(slotTime, 'yyyy-MM-dd'),
            displayDate: format(slotTime, 'EEE, MMM d, yyyy')
          });
        }
      }
      
      console.log('✅ Generated slots with overlap check:', { 
        totalSlots: slots.length,
        availableSlots: slots.filter(s => s.available).length,
        blockedSlots: slots.filter(s => !s.available && !allBookedTimes.has(s.date.toISOString())).length,
        bookedSlots: slots.filter(s => allBookedTimes.has(s.date.toISOString())).length
      });
      
      setAvailableSlots(slots);
    } catch (error) {
      console.error('Error generating available slots:', error);
      setAvailableSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  }, [confirmedBookings]);

  useEffect(() => {
    // Add animation styles
    const style = document.createElement('style');
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(100%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(100%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);

    // Initial fetch of available slots
    fetchAvailableSlots();

    // 🔒 CRITICAL: Listen for storage events from other tabs/windows
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === CONFIRMED_BOOKINGS_KEY && e.newValue) {
        try {
          const updatedBookings = new Set(JSON.parse(e.newValue));
          setConfirmedBookings(updatedBookings);
          // Immediately refresh slots to reflect cross-tab bookings
          fetchAvailableSlots(true);
        } catch (error) {
          console.error('Error handling storage change:', error);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);

    // Set up real-time subscription to calendar_bookings table
    const bookingsChannel = supabase
      .channel('calendar_bookings_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'calendar_bookings'
        },
        (payload) => {
          console.log('New booking detected:', payload);
          
          // 🔒 CRITICAL: Add booked slot to confirmed bookings immediately
          const bookedSlot = (payload.new as any)?.selected_time_slot;
          if (bookedSlot) {
            const bookedISO = new Date(bookedSlot).toISOString();
            setConfirmedBookings(prev => {
              const updated = new Set([...prev, bookedISO]);
              try {
                localStorage.setItem(
                  CONFIRMED_BOOKINGS_KEY,
                  JSON.stringify(Array.from(updated))
                );
              } catch (error) {
                console.error('Error updating localStorage on real-time event:', error);
              }
              return updated;
            });
          }
          
          // Force full reload with delay to ensure transaction commit
          setTimeout(() => {
            fetchAvailableSlots(true);
          }, 500);
        }
      )
      .subscribe((status) => {
        console.log('Bookings subscription status:', status);
      });

    // Cleanup
    return () => {
      document.head.removeChild(style);
      window.removeEventListener('storage', handleStorageChange);
      supabase.removeChannel(bookingsChannel);
    };
  }, [fetchAvailableSlots]);

  const handleSlotSelect = async (slot: TimeSlot) => {
    // CRITICAL: Absolute guard clause - prevent ANY interaction with unavailable slots
    if (!slot.available || isLoadingSlots || slot.isBeingBooked || bookingInProgress) {
      console.warn('Attempted to select unavailable slot:', slot);
      return;
    }
    
    // Real-time validation: Check if slot is still available in database
    try {
      const { data: existingBooking, error } = await supabase
        .from('calendar_bookings')
        .select('id')
        .eq('selected_time_slot', slot.date.toISOString())
        .maybeSingle();

      if (existingBooking) {
        showToast('This slot was just booked. Please select another time.', 'error');
        // Immediately refresh slots to show current availability
        await fetchAvailableSlots();
        return;
      }

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned (expected for available slots)
        console.error('Error checking slot availability:', error);
        showToast('Error checking slot availability. Please try again.', 'error');
        return;
      }
      
      setSelectedSlot(slot);
    } catch (error) {
      console.error('Error validating slot availability:', error);
      showToast('Error checking slot availability. Please try again.', 'error');
    }
  };

  const validateContactInfo = (): boolean => {
    const newErrors: Partial<ContactInfo> = {};
    
    if (!contactInfo.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!contactInfo.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!contactInfo.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(contactInfo.email)) {
      newErrors.email = 'Invalid email format';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleContactInfoChange = (field: keyof ContactInfo, value: string) => {
    setContactInfo(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const handleConfirmBooking = async () => {
    if (!selectedSlot) return;
    
    if (!validateContactInfo()) {
      showToast('Please fill in all required fields', 'error');
      return;
    }

    const slotKey = selectedSlot.date.toISOString();
    
    // 🔒 CRITICAL: FINAL PRE-BOOKING VALIDATION - Check one last time before submission
    try {
      const { data: finalCheck, error: checkError } = await supabase
        .from('calendar_bookings')
        .select('id')
        .eq('selected_time_slot', slotKey)
        .maybeSingle();

      if (finalCheck) {
        showToast('This slot was just booked. Refreshing available times...', 'error');
        setSelectedSlot(null);
        await fetchAvailableSlots(true);
        return;
      }

      if (checkError && checkError.code !== 'PGRST116') {
        console.error('Error in final availability check:', checkError);
        showToast('Error validating slot availability. Please try again.', 'error');
        return;
      }
    } catch (error) {
      console.error('Error in pre-booking validation:', error);
      showToast('Error validating slot availability. Please try again.', 'error');
      return;
    }

    setBookingInProgress(slotKey);
    
    // 🔒 CRITICAL: Immediately mark as unavailable in local state
    setAvailableSlots(prevSlots => 
      prevSlots.map(slot => 
        slot.date.toISOString() === slotKey 
          ? { ...slot, isBeingBooked: true, available: false }
          : slot
      )
    );

    setIsSubmitting(true);
    try {
      const { data: rpcData, error: rpcError } = await supabase.rpc(
        'record_public_calendar_booking',
        {
          p_contact_name: contactInfo.name,
          p_business_name: contactInfo.businessName,
          p_contact_email: contactInfo.email,
          p_contact_phone: contactInfo.phone || null,
          p_selected_time_slot: selectedSlot.date.toISOString(),
          p_booking_notes: null
        }
      );

      if (rpcError) {
        console.error('RPC error:', rpcError);
        throw new Error(rpcError.message || 'Failed to create calendar booking');
      }

      // Extract success result
      let success = false;
      let message = 'Operation completed';

      if (rpcData) {
        if (Array.isArray(rpcData) && rpcData.length > 0) {
          success = rpcData[0].success || false;
          message = rpcData[0].message || message;
        } else if (typeof rpcData === 'object') {
          success = rpcData.success || false;
          message = rpcData.message || message;
        }
      }

      if (success) {
        // 🔒 CRITICAL: Add to permanent local cache IMMEDIATELY AND persist to localStorage
        setConfirmedBookings(prev => {
          const updated = new Set([...prev, slotKey]);
          // Persist to localStorage immediately
          try {
            localStorage.setItem(
              CONFIRMED_BOOKINGS_KEY,
              JSON.stringify(Array.from(updated))
            );
          } catch (error) {
            console.error('Error persisting booking to localStorage:', error);
          }
          return updated;
        });
        
        showToast('Onboarding call scheduled successfully!', 'success');
        
        // Reset form and state
        setContactInfo({
          name: '',
          businessName: '',
          email: '',
          phone: ''
        });
        setSelectedSlot(null);
        setBookingInProgress(null);
        
        // 🔒 CRITICAL: Broadcast the booking to ALL other tabs/windows
        try {
          const bookingEvent = new StorageEvent('storage', {
            key: CONFIRMED_BOOKINGS_KEY,
            newValue: JSON.stringify(Array.from(new Set([...confirmedBookings, slotKey]))),
            url: window.location.href
          });
          window.dispatchEvent(bookingEvent);
        } catch (error) {
          console.error('Error broadcasting booking event:', error);
        }
        
        // Force refresh slots (now with local cache + localStorage protection)
        await fetchAvailableSlots(true);
      } else {
        // Show the actual error message from the database function
        showToast(message || 'Failed to schedule call. Please try another time slot.', 'error');
        
        // 🔒 CRITICAL: Remove from optimistic UI on failure
        setAvailableSlots(prevSlots => 
          prevSlots.map(slot => 
            slot.date.toISOString() === slotKey 
              ? { ...slot, isBeingBooked: false, available: true }
              : slot
          )
        );
        
        setBookingInProgress(null);
        setSelectedSlot(null);
        await fetchAvailableSlots(true);
      }
    } catch (error: any) {
      console.error('Booking error:', error);
      showToast(error.message || 'Error scheduling onboarding call. Please try again.', 'error');
      
      // 🔒 CRITICAL: Remove from optimistic UI on error
      setAvailableSlots(prevSlots => 
        prevSlots.map(slot => 
          slot.date.toISOString() === slotKey 
            ? { ...slot, isBeingBooked: false, available: true }
            : slot
        )
      );
      
      setBookingInProgress(null);
      setSelectedSlot(null);
      await fetchAvailableSlots(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Group slots by date
  const slotsByDate = availableSlots.reduce((acc, slot) => {
    if (!acc[slot.dateKey]) {
      acc[slot.dateKey] = {
        displayDate: slot.displayDate,
        slots: []
      };
    }
    acc[slot.dateKey].slots.push(slot);
    return acc;
  }, {} as Record<string, { displayDate: string; slots: TimeSlot[] }>);

  const isBookingReady = selectedSlot && contactInfo.name && contactInfo.businessName && contactInfo.email;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-white to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4">
          <h1 className="text-xl font-bold text-gray-900">Schedule Partnership Discussion</h1>
          <p className="text-sm text-gray-600 mt-0.5">Book your 45-minute onboarding call - No account required</p>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Calendar Selection */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-md p-4">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Calendar className="w-5 h-5 mr-2 text-purple-600" />
                Available Time Slots
              </h2>

              {isLoadingSlots ? (
                <div className="flex flex-col items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 text-purple-600 animate-spin mb-3" />
                  <p className="text-sm text-gray-600">Loading available time slots...</p>
                </div>
              ) : availableSlots.length === 0 ? (
                <div className="text-center py-12">
                  <Calendar className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm text-gray-600">No available time slots found</p>
                  <p className="text-xs text-gray-400 mt-1">Please check back later</p>
                </div>
              ) : (
                <div className="space-y-4 max-h-[600px] overflow-y-auto pr-2">
                  {Object.entries(slotsByDate).map(([dateKey, dateGroup]) => (
                    <div key={dateKey} className="border-b border-gray-100 pb-3 last:border-0">
                      <h3 className="font-medium text-gray-900 mb-2 text-sm">
                        {dateGroup.displayDate}
                      </h3>
                      <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 gap-1.5">
                        {dateGroup.slots.map((slot, index) => {
                          const isCurrentlyBooking = slot.isBeingBooked || bookingInProgress === slot.date.toISOString();
                          const isTrulyUnavailable = !slot.available || isCurrentlyBooking;
                          
                          return (
                            <button
                              key={index}
                              onClick={() => handleSlotSelect(slot)}
                              disabled={isTrulyUnavailable || isLoadingSlots}
                              className={`
                                p-2 rounded-md border-2 transition-all text-xs font-medium relative
                                ${selectedSlot?.date.toISOString() === slot.date.toISOString()
                                  ? 'border-purple-600 bg-purple-50 text-purple-700 shadow-sm'
                                  : isCurrentlyBooking
                                  ? 'border-yellow-300 bg-yellow-50 text-yellow-600 cursor-wait animate-pulse pointer-events-none'
                                  : slot.available && !isLoadingSlots
                                  ? 'border-gray-200 hover:border-purple-300 hover:bg-purple-50 text-gray-700 cursor-pointer' :'border-red-300 bg-red-100 text-red-600 cursor-not-allowed opacity-70 pointer-events-none'
                                }
                              `}
                              style={
                                isTrulyUnavailable 
                                  ? { pointerEvents: 'none', userSelect: 'none' } 
                                  : {}
                              }
                              title={
                                isCurrentlyBooking 
                                  ? 'Booking in progress...' 
                                  : !slot.available 
                                  ? 'UNAVAILABLE - This time slot is already booked' :'Click to select this time slot'
                              }
                              aria-disabled={isTrulyUnavailable}
                              aria-label={`Time slot ${slot.time} on ${slot.displayDate} - ${isTrulyUnavailable ? 'Unavailable' : 'Available'}`}
                            >
                              <span className={!slot.available && !isCurrentlyBooking ? 'line-through' : ''}>
                                {slot.time}
                              </span>
                              {isCurrentlyBooking && (
                                <span className="absolute inset-0 flex items-center justify-center text-[0.45rem] font-bold text-yellow-700 pointer-events-none bg-yellow-50 bg-opacity-90 rounded-md">
                                  BOOKING...
                                </span>
                              )}
                              {!slot.available && !isCurrentlyBooking && (
                                <span className="absolute inset-0 flex items-center justify-center text-[0.5rem] font-bold text-red-700 pointer-events-none bg-red-100 bg-opacity-95 rounded-md">
                                  BOOKED
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Contact Info & Booking Summary */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-md p-4 sticky top-20">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">Contact Information</h2>

              {/* Contact Information Form */}
              <div className="space-y-3 mb-4 pb-4 border-b border-gray-200">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <User className="w-3 h-3 inline mr-1" />
                    Full Name *
                  </label>
                  <Input
                    type="text"
                    value={contactInfo.name}
                    onChange={(e) => handleContactInfoChange('name', e.target.value)}
                    placeholder="John Doe"
                    className={`text-sm ${errors.name ? 'border-red-300' : ''}`}
                  />
                  {errors.name && <p className="text-xs text-red-600 mt-1">{errors.name}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Building2 className="w-3 h-3 inline mr-1" />
                    Business Name *
                  </label>
                  <Input
                    type="text"
                    value={contactInfo.businessName}
                    onChange={(e) => handleContactInfoChange('businessName', e.target.value)}
                    placeholder="Acme Corporation"
                    className={`text-sm ${errors.businessName ? 'border-red-300' : ''}`}
                  />
                  {errors.businessName && <p className="text-xs text-red-600 mt-1">{errors.businessName}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Mail className="w-3 h-3 inline mr-1" />
                    Email Address *
                  </label>
                  <Input
                    type="email"
                    value={contactInfo.email}
                    onChange={(e) => handleContactInfoChange('email', e.target.value)}
                    placeholder="john@acme.com"
                    className={`text-sm ${errors.email ? 'border-red-300' : ''}`}
                  />
                  {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    <Phone className="w-3 h-3 inline mr-1" />
                    Phone Number (Optional)
                  </label>
                  <Input
                    type="tel"
                    value={contactInfo.phone}
                    onChange={(e) => handleContactInfoChange('phone', e.target.value)}
                    placeholder="+43 1 234 5678"
                    className="text-sm"
                  />
                </div>
              </div>

              {/* Selected Slot Summary */}
              {selectedSlot ? (
                <div className="space-y-3">
                  <div className="bg-purple-50 rounded-md p-3">
                    <div className="flex items-center text-sm">
                      <Calendar className="w-4 h-4 text-purple-600 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-600">Selected Date</p>
                        <p className="font-semibold text-gray-900">
                          {format(selectedSlot.date, 'EEE, MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-indigo-50 rounded-md p-3">
                    <div className="flex items-center text-sm">
                      <Clock className="w-4 h-4 text-indigo-600 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-xs text-gray-600">Time Slot</p>
                        <p className="font-semibold text-gray-900">
                          {format(selectedSlot.date, 'HH:mm')} <span className="text-xs font-normal text-gray-600">(45 min)</span>
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 rounded-md p-3">
                    <div className="flex items-start text-sm">
                      <CheckCircle2 className="w-4 h-4 text-green-600 mr-2 flex-shrink-0 mt-0.5" />
                      <div>
                        <p className="text-xs text-gray-600 mb-1">Call Agenda</p>
                        <ul className="text-xs text-gray-700 space-y-0.5">
                          <li>• Partnership overview</li>
                          <li>• Platform benefits demo</li>
                          <li>• Q&A session</li>
                          <li>• Next steps discussion</li>
                        </ul>
                      </div>
                    </div>
                  </div>

                  <Button
                    onClick={handleConfirmBooking}
                    disabled={isSubmitting || !isBookingReady}
                    className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white disabled:opacity-50 disabled:cursor-not-allowed text-sm py-2.5"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Confirming...
                      </>
                    ) : (
                      <>
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Confirm Booking
                      </>
                    )}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-6">
                  <Calendar className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                  <p className="text-sm text-gray-500">Select a time slot to continue</p>
                  <p className="text-xs text-gray-400 mt-1">All bookings are subject to confirmation</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}